import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SEOHead, breadcrumbSchema, serviceSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/services/${slug}`),
      axios.get(`${API}/services`),
      axios.get(`${API}/locations`),
    ]).then(([svcRes, allRes, locRes]) => {
      setService(svcRes.data);
      setRelated(allRes.data.filter(s => s.slug !== slug).slice(0, 4));
      setAreas((locRes.data || []).slice(0, 6));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-black border-t-[#FFD600] rounded-full animate-spin mx-auto mb-4" />
        <p className="font-bold">Loading...</p>
      </div>
    </main>
  );

  if (!service) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-black mb-4">Service Not Found</h1>
        <Link to="/services" className="btn-primary">View All Services</Link>
      </div>
    </main>
  );

  return (
    <main className="pt-16" data-testid="service-detail-page">
      <SEOHead
        title={service.title}
        description={service.excerpt}
        path={`/services/${service.slug}`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
          serviceSchema(service.title, service.excerpt, 499),
        ]}
      />
      <Breadcrumbs items={[
        { name: "Services", path: "/services" },
        { name: service.title, path: `/services/${service.slug}` },
      ]} />

      <div className="container-main max-w-3xl py-16">
        <Link to="/services" className="inline-flex items-center gap-2 font-bold text-sm mb-8 hover:text-[#0055FF] transition-colors" data-testid="back-to-services">
          <ArrowLeft size={14} /> All Services
        </Link>

        <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="service-title">
          {service.title}
        </h1>

        <p className="text-lg text-gray-600 mb-10 pb-8 border-b-2 border-black">{service.excerpt}</p>

        <div
          className="blog-content text-gray-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: service.content }}
          data-testid="service-content"
        />

        <div className="mt-16 bg-[#FFD600] border-2 border-black rounded-2xl p-8 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="text-2xl font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Ready to Get Your Website?
          </h3>
          <p className="font-semibold mb-6">Professional websites from $499. Live in 48 hours. 100% Aussie support.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-secondary inline-flex items-center gap-2" data-testid="service-quote-cta">
              Get a Free Quote <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="btn-primary inline-flex items-center gap-2" data-testid="service-packages-cta">
              View Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {areas.length > 0 && (
        <section className="section-padding border-t-4 border-black">
          <div className="container-main">
            <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Areas We Serve</h2>
            <p className="text-gray-600 mb-8 font-semibold">Local web design across Melbourne and Victoria.</p>
            <div className="flex flex-wrap gap-3">
              {areas.map((a, i) => (
                <Link
                  key={a.id || i}
                  to={`/areas/${a.slug}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-black rounded-full font-bold text-sm hover:bg-[#FFD600] transition-colors"
                  data-testid={`service-area-link-${i}`}
                >
                  {a.suburb} <ArrowRight size={12} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="section-padding bg-[#F4F4F5] border-t-4 border-black">
          <div className="container-main">
            <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>More Industries We Serve</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((s, i) => (
                <Link key={s.id || i} to={`/services/${s.slug}`} className="group card-brutal" data-testid={`related-service-${i}`}>
                  <h3 className="font-black text-lg mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{s.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm font-bold text-[#0055FF]">Learn More <ArrowRight size={12} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
